const STATE_TO_ABBREVIATION: Record<string, string> = {
  washington: 'WA',
  oregon: 'OR',
  california: 'CA',
  indiana: 'IN',
  texas: 'TX',
  tennessee: 'TN',
  'north carolina': 'NC',
  nebraska: 'NE',
  wisconsin: 'WI',
  'puerto rico': 'PR',
};

const SUPPORTED_CITY_STATE_ALLOWLIST: Array<{ city: string; state: string }> = [
  { city: 'seattle', state: 'wa' },
  { city: 'tacoma', state: 'wa' },
  { city: 'kent', state: 'wa' },
  { city: 'renton', state: 'wa' },
  { city: 'auburn', state: 'wa' },
  { city: 'redmond', state: 'wa' },
  { city: 'indianapolis', state: 'in' },
  { city: 'dallas', state: 'tx' },
  { city: 'lebanon', state: 'tn' },
  { city: 'geneva', state: 'ne' },
];

function normalizeStateCode(stateText?: string): string | undefined {
  const raw = (stateText || '').trim();
  if (!raw) return undefined;
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  const mapped = STATE_TO_ABBREVIATION[raw.toLowerCase()];
  return mapped || raw.toUpperCase();
}

export function parseCityStateFromAddress(addressText: string): { city?: string; state?: string } {
  const text = (addressText || '').trim();
  if (!text) return {};
  const cityStateMatch = text.match(
    /,\s*([^,]+),\s*([A-Za-z]{2}|[A-Za-z][A-Za-z\s]+?)(?:\s+\d{5}(?:-\d{4})?)?\s*$/,
  );
  if (!cityStateMatch) return {};
  return {
    city: cityStateMatch[1]?.trim(),
    state: normalizeStateCode(cityStateMatch[2]),
  };
}

export function isSupportedCityState(city?: string, state?: string): boolean {
  const normalizedCity = (city || '').trim().toLowerCase();
  const normalizedState = (state || '').trim().toLowerCase();
  if (!normalizedCity || !normalizedState) {
    return false;
  }
  return SUPPORTED_CITY_STATE_ALLOWLIST.some(
    (entry) => entry.city === normalizedCity && entry.state === normalizedState,
  );
}

export function getSupportedCitiesList(): string[] {
  return SUPPORTED_CITY_STATE_ALLOWLIST.map(
    (entry) => `${entry.city.charAt(0).toUpperCase()}${entry.city.slice(1)}, ${entry.state.toUpperCase()}`,
  );
}

export function getSupportedCitiesLabel(): string {
  return getSupportedCitiesList().join(', ');
}

// Cities for which the backend supports APN-first project creation today.
// Keep in sync with PARCEL_MODE_CITY_KEYS in backend/shared/sela_projects_store.py.
export type ParcelSupportedCity = { city: string; state: string; label: string };

export const PARCEL_MODE_SUPPORTED_CITIES: ParcelSupportedCity[] = [
  { city: 'seattle', state: 'wa', label: 'Seattle, WA' },
];

export function isSupportedParcelCity(city?: string, state?: string): boolean {
  const normalizedCity = (city || '').trim().toLowerCase();
  const normalizedState = (state || '').trim().toLowerCase();
  if (!normalizedCity || !normalizedState) {
    return false;
  }
  return PARCEL_MODE_SUPPORTED_CITIES.some(
    (entry) => entry.city === normalizedCity && entry.state === normalizedState,
  );
}

export type AddressSuggestion = {
  label: string;
  lat: number;
  lng: number;
};

export type AddressGeocode = {
  lat: number;
  lng: number;
  label: string;
};

function formatNominatimAddress(item: { address?: Record<string, string> }): string | null {
  const itemAddress = item.address;
  if (!itemAddress) return null;
  const houseNumber = itemAddress.house_number ? `${itemAddress.house_number} ` : '';
  const road = itemAddress.road || '';
  if (!houseNumber.trim() && !road) return null;
  const city = itemAddress.city || itemAddress.town || itemAddress.village || itemAddress.county || '';
  const stateRaw = itemAddress.state_code || itemAddress.state || '';
  const state = stateRaw.includes('-') ? stateRaw.split('-').pop() : stateRaw;
  const postcode = itemAddress.postcode ? ` ${itemAddress.postcode}` : '';
  const formatted = `${houseNumber}${road}, ${city}, ${state}${postcode}`.replace(/\s+/g, ' ').trim();
  return formatted.includes(',') ? formatted : null;
}

export async function fetchAddressSuggestions(query: string, signal: AbortSignal): Promise<AddressSuggestion[]> {
  if (!query.trim()) {
    return [];
  }

  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=10&addressdetails=1&countrycodes=us`;
  const response = await fetch(url, {
    headers: { Accept: 'application/json' },
    signal,
  });

  if (!response.ok) {
    throw new Error('Failed to fetch suggestions');
  }

  const data = await response.json();
  const seen = new Set<string>();
  const results: AddressSuggestion[] = [];

  for (const item of data) {
    const label = formatNominatimAddress(item);
    const lat = Number.parseFloat(item.lat);
    const lng = Number.parseFloat(item.lon);
    if (!label || !Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    if (seen.has(label)) continue;
    seen.add(label);
    results.push({ label, lat, lng });
  }

  return results;
}

/** Resolve coordinates for a full address string (e.g. restored onboarding state). */
export async function geocodeAddressLabel(label: string, signal: AbortSignal): Promise<AddressGeocode | null> {
  const trimmed = label.trim();
  if (!trimmed) return null;
  const suggestions = await fetchAddressSuggestions(trimmed, signal);
  const exact = suggestions.find((item) => item.label === trimmed);
  const match = exact || suggestions[0];
  if (!match) return null;
  return { lat: match.lat, lng: match.lng, label: match.label };
}
